import React, { useState, useEffect } from 'react';
import { Layout, Menu, message, ConfigProvider } from 'antd';
import zhCN from 'antd/locale/zh_CN';
import dayjs from 'dayjs';
import 'dayjs/locale/zh-cn';
import {
  DashboardOutlined,
  UserOutlined,
  ShoppingCartOutlined,
  BarChartOutlined,
  SettingOutlined,
  MailOutlined
} from '@ant-design/icons';
import AccountList from './components/AccountList';
import AccountForm from './components/AccountForm';
import SaleList from './components/SaleList';
import SaleForm from './components/SaleForm';
import Dashboard from './components/Dashboard';
import Reports from './components/Reports';
import EmailCenter from './components/EmailCenter';
import SystemSettings from './components/SystemSettings';
import { getStats } from './services/api';
import useResponsive from './hooks/useResponsive';
import LoginGate from './components/LoginGate';
import { getMe, logout as apiLogout } from './services/api';

const { Header, Sider, Content } = Layout;

function App() {
  dayjs.locale('zh-cn');
  const { isMobile } = useResponsive();
  const [collapsed, setCollapsed] = useState(false);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState('dashboard');
  const [authed, setAuthed] = useState(false);
  const [sessionTimeout, setSessionTimeout] = useState(null);

  useEffect(() => {
    // 检查本地存储的认证状态
    const token = localStorage.getItem('token');
    if (token) {
      // 检查token是否过期
      try {
        const tokenData = JSON.parse(atob(token));
        const tokenAge = Date.now() - tokenData.timestamp;
        const maxAge = 24 * 60 * 60 * 1000; // 24小时
        
        if (tokenAge > maxAge) {
          // Token过期，清除并重新登录
          localStorage.removeItem('token');
          setAuthed(false);
          message.warning('登录已过期，请重新登录');
        } else {
          setAuthed(true);
          fetchStats();
          
          // 设置会话超时提醒（提前5分钟）
          const remainingTime = maxAge - tokenAge;
          const warningTime = 5 * 60 * 1000; // 5分钟
          
          if (remainingTime > warningTime) {
            const timeoutId = setTimeout(() => {
              message.warning('您的登录即将在5分钟后过期，请及时保存工作');
            }, remainingTime - warningTime);
            setSessionTimeout(timeoutId);
          }
        }
      } catch (error) {
        // Token无效，清除并重新登录
        localStorage.removeItem('token');
        setAuthed(false);
        message.error('认证信息无效，请重新登录');
      }
    } else {
      setAuthed(false);
    }
  }, []);

  // 清理定时器
  useEffect(() => {
    return () => {
      if (sessionTimeout) {
        clearTimeout(sessionTimeout);
      }
    };
  }, [sessionTimeout]);

  const fetchStats = async () => {
    try {
      const data = await getStats();
      setStats(data);
    } catch (error) {
      message.error('获取统计数据失败');
    } finally {
      setLoading(false);
    }
  };

  const menuItems = [
    {
      key: 'dashboard',
      icon: <DashboardOutlined />,
      label: '仪表板',
    },
    {
      key: 'accounts-group',
      icon: <UserOutlined />,
      label: '账号管理',
      children: [
        { key: 'accounts', label: '账号列表' },
        { key: 'accounts-white', label: '白号库存' },
      ],
    },
    {
      key: 'sales',
      icon: <ShoppingCartOutlined />,
      label: '销售管理',
    },
    {
      key: 'reports',
      icon: <BarChartOutlined />,
      label: '数据报表',
    },
    {
      key: 'email-group',
      icon: <MailOutlined />,
      label: '域名邮箱',
      children: [
        { key: 'email', label: '仪表盘' },
        { key: 'email/accounts', label: '账号管理' },
        { key: 'email/mails', label: '邮件管理' },
      ],
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: '系统设置',
    },
  ];

  const handleMenuClick = ({ key }) => {
    if (key === 'email-group' || key === 'accounts-group') return;
    setCurrentPage(key);
    if (key === 'dashboard' || key === 'reports') {
      fetchStats();
    }
  };

  const handleDataChanged = () => {
    // 数据变化时刷新统计
    fetchStats();
  };

  const handleNavigate = (page) => {
    setCurrentPage(page);
  };

  const renderContent = () => {
    // 处理带参数的路由
    if (currentPage.startsWith('accounts/')) {
      const accountId = currentPage.split('/')[1];
      return <AccountForm accountId={accountId} onSuccess={() => setCurrentPage('accounts')} onCancel={() => setCurrentPage('accounts')} />;
    }
    
    if (currentPage.startsWith('sales/new')) {
      const urlParams = new URLSearchParams(currentPage.split('?')[1] || '');
      const accountId = urlParams.get('accountId');
      return <SaleForm accountId={accountId} onSuccess={() => setCurrentPage('sales')} onCancel={() => setCurrentPage('sales')} />;
    }

    switch (currentPage) {
      case 'dashboard':
        return <Dashboard stats={stats} loading={loading} isMobile={isMobile} />;
      case 'reports':
        return <Reports isMobile={isMobile} />;
      case 'email':
      case 'email/accounts':
      case 'email/mails': {
        const emailTab =
          currentPage === 'email/accounts' ? 'accounts' :
          currentPage === 'email/mails' ? 'mails' : 'overview';
        return (
          <EmailCenter
            isMobile={isMobile}
            initialTab={emailTab}
            onNavigate={handleNavigate}
          />
        );
      }
      case 'accounts':
        return <AccountList key="accounts-normal" onNavigate={handleNavigate} onDataChanged={fetchStats} mode="normal" />;
      case 'accounts-white':
        return <AccountList key="accounts-white" onNavigate={handleNavigate} onDataChanged={fetchStats} mode="white" />;
      case 'sales':
        return <SaleList onNavigate={handleNavigate} onDataChanged={fetchStats} />;
      case 'settings':
      case 'settings/auth':
        return <SystemSettings onUpdated={() => setAuthed(false)} />;
      default:
        return <Dashboard stats={stats} loading={loading} isMobile={isMobile} />;
    }
  };

  if (!authed) {
    return (
      <ConfigProvider locale={zhCN}>
        <LoginGate onSuccess={() => { setAuthed(true); setCurrentPage('dashboard'); fetchStats(); }} />
      </ConfigProvider>
    );
  }

  return (
    <ConfigProvider locale={zhCN}>
    <Layout style={{ minHeight: '100vh' }}>
      {!isMobile && (
        <Sider
          collapsible
          collapsed={collapsed}
          onCollapse={setCollapsed}
          theme="light"
          width={200}
          breakpoint="lg"
          onBreakpoint={(broken) => setCollapsed(broken)}
        >
          <div className="logo" style={{ 
            height: '64px', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            fontSize: collapsed ? '16px' : '18px',
            fontWeight: 'bold',
            color: '#1890ff',
            borderBottom: '1px solid #f0f0f0'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <img
                src={'/logo-tiktok.png?v=1'}
                onError={(e)=>{ const t=e.currentTarget; if(!t.dataset.f1){ t.dataset.f1='1'; t.src='/logo-tiktok.jpg?v=1'; } else { t.onerror=null; t.style.display='none'; }}}
                alt="TikTok"
                style={{ width: 24, height: 24, borderRadius: 6 }}
              />
              <span style={{ color: '#1890ff' }}>TikTok账号管理ERP</span>
            </div>
          </div>
          <Menu
            mode="inline"
            selectedKeys={[
              currentPage.startsWith('settings')
                ? 'settings'
                : currentPage.startsWith('accounts/')
                  ? 'accounts'
                  : currentPage
            ]}
            defaultOpenKeys={['email-group', 'accounts-group']}
            items={menuItems}
            style={{ borderRight: 0 }}
            onClick={handleMenuClick}
          />
        </Sider>
      )}
      <Layout>
        <Header style={{ 
          background: '#fff', 
          padding: isMobile ? '0 16px' : '0 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          height: isMobile ? '56px' : '64px'
        }}>
          {isMobile ? (
            <div style={{ display:'flex', alignItems:'center', gap:10 }}>
              <img
                src={'/logo-tiktok.png?v=1'}
                onError={(e)=>{ const t=e.currentTarget; if(!t.dataset.f1){ t.dataset.f1='1'; t.src='/logo-tiktok.jpg?v=1'; } else { t.onerror=null; t.style.display='none'; }}}
                alt="TikTok"
                style={{ width: 22, height: 22, borderRadius: 6 }}
              />
              <h2 style={{ margin: 0, color: '#1890ff', fontSize: 18 }}>TikTok账号管理ERP</h2>
            </div>
          ) : (
            <div />
          )}
          <button
            onClick={async () => { 
              try {
                await apiLogout();
                // 清除本地状态
                localStorage.removeItem('token');
                setAuthed(false);
                // 清理定时器
                if (sessionTimeout) {
                  clearTimeout(sessionTimeout);
                  setSessionTimeout(null);
                }
                message.success('已退出登录');
              } catch (error) {
                console.error('退出登录失败:', error);
                // 即使API调用失败，也清除本地状态
                localStorage.removeItem('token');
                setAuthed(false);
                if (sessionTimeout) {
                  clearTimeout(sessionTimeout);
                  setSessionTimeout(null);
                }
                message.success('已退出登录');
              }
            }}
            style={{ border: '1px solid #f0f0f0', background: '#fff', padding: isMobile ? '4px 8px' : '6px 10px', borderRadius: 6, cursor: 'pointer' }}
          >
            退出登录
          </button>
        </Header>
        <Content style={{ margin: isMobile ? '12px' : '24px', marginBottom: isMobile ? 76 : 24, background: '#fff', borderRadius: '8px' }}>
          {renderContent()}
        </Content>
      </Layout>

      {/* 移动端底部 Tab 导航 */}
      {isMobile && (
        <div style={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 0,
          background: '#fff',
          borderTop: '1px solid #f0f0f0',
          zIndex: 1000
        }}>
          <Menu
            mode="horizontal"
            selectedKeys={[currentPage.startsWith('settings') ? 'settings' : currentPage]}
            onClick={handleMenuClick}
            style={{ display: 'flex', justifyContent: 'space-around' }}
            items={[
              { key: 'dashboard', icon: <DashboardOutlined />, label: '首页' },
              { key: 'accounts', icon: <UserOutlined />, label: '账号' },
              { key: 'sales', icon: <ShoppingCartOutlined />, label: '销售' },
              { key: 'settings', icon: <SettingOutlined />, label: '设置' },
            ]}
          />
        </div>
      )}
    </Layout>
    </ConfigProvider>
  );
}

export default App;
